import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { ArrowLeftIcon, PencilIcon, Trash2Icon, XIcon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

interface DriverDetail {
  id: string
  code: string
  name: string
  sgtCodes: string[] | null
  fileId: number | null
  dni: string | null
  docTypeId: string | null
  docTypeName: string | null
  docId: string | null
  isOwned: boolean
  pvcuenta: number | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

function fmtDate(v: string) {
  return new Date(v).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function Field({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm" : "text-sm"}>
        {value ?? <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  )
}

export default function DriverDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [editOpen,   setEditOpen]   = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [form,       setForm]       = useState({ code: "", name: "" })
  const [sgtCodes,   setSgtCodes]   = useState<string[]>([])
  const [sgtInput,   setSgtInput]   = useState("")
  const [formError,  setFormError]  = useState<string | null>(null)

  // ── Query ─────────────────────────────────────────────────────────────────

  const { data: driver, isLoading, isError } = useQuery({
    queryKey: ["driver", id],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/providers/drivers/${id}`)
      if (!res.ok) throw new Error("Conductor no encontrado")
      return res.json() as Promise<DriverDetail>
    },
    enabled: !!id,
  })

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updateMut = useMutation({
    mutationFn: async (data: { code: string; name: string; sgtCodes: string[] | null }) => {
      const res = await authorizedFetch(`${API_URL}/api/providers/drivers/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar")
      }
      return res.json() as Promise<DriverDetail>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["driver", id] })
      qc.invalidateQueries({ queryKey: ["drivers"] })
      setEditOpen(false)
      setFormError(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/providers/drivers/${id}`, { method: "DELETE" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["driver", id] })
      qc.invalidateQueries({ queryKey: ["drivers"] })
      setDeleteOpen(false)
      navigate("/drivers")
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/providers/drivers/${id}/restore`, { method: "PATCH" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["driver", id] })
      qc.invalidateQueries({ queryKey: ["drivers"] })
    },
    onError: (err: Error) => setFormError(err.message),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────

  function openEdit() {
    if (!driver) return
    setForm({ code: driver.code, name: driver.name })
    setSgtCodes(driver.sgtCodes ?? [])
    setSgtInput("")
    setFormError(null)
    setEditOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.code.trim() || !form.name.trim()) {
      setFormError("Código y nombre son requeridos")
      return
    }
    updateMut.mutate({
      code:     form.code.trim().toUpperCase(),
      name:     form.name.trim(),
      sgtCodes: sgtCodes.length ? sgtCodes : null,
    })
  }

  // ── Loading / error ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <>
        <SiteHeader title="Conductor" />
        <div className="flex flex-1 items-center justify-center py-24 text-sm text-muted-foreground">
          Cargando...
        </div>
      </>
    )
  }

  if (isError || !driver) {
    return (
      <>
        <SiteHeader title="Conductor no encontrado" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
          <p className="text-sm text-muted-foreground">No se encontró el conductor.</p>
          <Button variant="outline" onClick={() => navigate("/drivers")}>
            <ArrowLeftIcon className="size-4 mr-2" /> Volver a conductores
          </Button>
        </div>
      </>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title={driver.name} />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-2xl space-y-6">

          {/* Barra superior */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <ArrowLeftIcon className="size-4" /> Volver
              </button>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded uppercase tracking-wide">
                {driver.code}
              </span>
              {driver.deletedAt && (
                <Badge className="bg-destructive/10 text-destructive border-destructive/20">
                  Eliminado
                </Badge>
              )}
            </div>

            {can("write", "drivers") && (
              <div className="flex items-center gap-2">
                {driver.deletedAt ? (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => restoreMut.mutate()}
                    disabled={restoreMut.isPending}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  >
                    <RestoreIcon className="size-4 mr-1.5" />
                    {restoreMut.isPending ? "Restaurando..." : "Restaurar"}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={openEdit}>
                      <PencilIcon className="size-4 mr-1.5" /> Editar
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setDeleteOpen(true)}
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    >
                      <Trash2Icon className="size-4 mr-1.5" /> Eliminar
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Identificación */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Identificación
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Código"    value={driver.code} mono />
              <Field label="Nombre"    value={driver.name} />
              <Field label="Legajo"    value={driver.fileId?.toString() ?? null} mono />
              <Field label="DNI"       value={driver.dni} mono />
              <Field label="Doc ID"    value={driver.docId} mono />
              <Field label="Tipo doc"  value={driver.docTypeName} />
              <Field label="Propio"    value={driver.isOwned ? "Sí" : "No"} />
              <Field label="Pv. Cuenta" value={driver.pvcuenta?.toString() ?? null} mono />
            </dl>
          </div>

          {/* Códigos SGT */}
          <div className="rounded-lg border p-5 space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Códigos SGT (SmartGT)
            </h3>
            {driver.sgtCodes?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {driver.sgtCodes.map((s) => (
                  <span key={s} className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{s}</span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground/50">Sin códigos SGT asignados</p>
            )}
          </div>

          {/* Registro */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Registro
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Creado"      value={fmtDate(driver.createdAt)} />
              <Field label="Actualizado" value={fmtDate(driver.updatedAt)} />
              {driver.deletedAt && (
                <Field label="Eliminado" value={fmtDate(driver.deletedAt)} />
              )}
            </dl>
          </div>

        </div>
      </div>

      {/* Edit Sheet */}
      <Sheet open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setFormError(null) } }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>Editar conductor</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

            <div className="grid gap-1.5">
              <Label htmlFor="dr-code">Código</Label>
              <Input
                id="dr-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={10}
                className="font-mono uppercase"
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="dr-name">Nombre</Label>
              <Input
                id="dr-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={200}
                required
              />
            </div>

            {/* Códigos SGT */}
            <div className="grid gap-1.5">
              <Label>Códigos SGT</Label>
              <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
                {sgtCodes.map((code, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                    {code}
                    <button
                      type="button"
                      onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <XIcon className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={sgtInput}
                  onChange={(e) => setSgtInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault()
                      const val = sgtInput.toUpperCase().trim()
                      if (val && !sgtCodes.includes(val)) setSgtCodes([...sgtCodes, val])
                      setSgtInput("")
                    }
                  }}
                  placeholder={sgtCodes.length === 0 ? "Escribir y Enter para agregar..." : ""}
                  className="flex-1 min-w-28 bg-transparent text-xs font-mono outline-none placeholder:text-muted-foreground"
                />
              </div>
              <p className="text-xs text-muted-foreground">Nombres exactos en SmartGT. Presioná Enter o coma para agregar.</p>
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={updateMut.isPending}>
                {updateMut.isPending ? "Guardando..." : "Guardar cambios"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar conductor</DialogTitle>
            <DialogDescription>
              ¿Eliminás <strong>{driver.code}</strong> — {driver.name}? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
